import { __, sprintf } from '@wordpress/i18n';
import {
  useBlockProps,
  useInnerBlocksProps,
  InspectorControls,
  BlockContextProvider,
  store as blockEditorStore,
} from '@wordpress/block-editor';
import { Notice, Placeholder, PanelBody } from '@wordpress/components';
import { createBlock } from '@wordpress/blocks';
import { useDispatch, useSelect } from '@wordpress/data';
import { store as coreStore } from '@wordpress/core-data';
import { useEffect, useRef } from '@wordpress/element';
import { gallery as galleryIcon } from '@wordpress/icons';

import DisplayPanel from './InspectorControls/DisplayPanel';
import TitlePositionPanel from './InspectorControls/TitlePositionPanel';
import LightboxPanel from './InspectorControls/LightboxPanel';
import SourcePanel from './InspectorControls/SourcePanel';
import QueryPanel from './InspectorControls/QueryPanel';
import ItemAppender from './ItemAppender';
import SliderControlsPanel from '../../../utils/slider-controls-panel';
import { getBlockGapValue } from '../../../utils/helpers';
import { controlColorVars } from '../../../utils/slider-control-colors';
import SliderControlsSkeleton, {
  navHostClass,
  previewDotCount,
} from '../../../utils/slider-controls-skeleton';
import { MEDIA_GALLERY_STARTER_ITEMS } from '../../_shared/media-gallery-item-defaults';

import '../editor.scss';

const ITEM = 'brandy/media-gallery-item';
// Three demo-filled items, matching the inserter preview. Bare [[ITEM]] entries
// would render as lone image placeholders with no text fields.
const STATIC_TEMPLATE = MEDIA_GALLERY_STARTER_ITEMS;
// Inner-block layout for the single-item dynamic template. Each post in the
// query renders this template with post context swapped (server-side).
const DYNAMIC_INNER = [
  ['core/post-featured-image', {}],
  ['core/post-title', { level: 4 }],
  ['core/post-excerpt', {}],
];
const SOFT_WARN_THRESHOLD = 30;

export default function Edit({ attributes, setAttributes, clientId }) {
  const { displayMode, columns, gap, titlePosition, isDynamic = false } = attributes;
  const blockGap = getBlockGapValue(attributes.style?.spacing?.blockGap);
  const perPage = attributes.query?.perPage ?? 6;
  const previewPostType = attributes.query?.postType ?? 'post';

  const { replaceInnerBlocks } = useDispatch(blockEditorStore);

  // On transition INTO dynamic mode, replace the inner-block tree with a single
  // gallery-item containing the post-* template. Toggling back to static does
  // NOT auto-restore manual items.
  const prevIsDynamic = useRef(isDynamic);
  useEffect(() => {
    if (prevIsDynamic.current === isDynamic) return;
    prevIsDynamic.current = isDynamic;
    if (isDynamic) {
      const inner = DYNAMIC_INNER.map(([name, attrs]) => createBlock(name, attrs));
      const item = createBlock(ITEM, {}, inner);
      replaceInnerBlocks(clientId, [item]);
    }
  }, [isDynamic, clientId, replaceInnerBlocks]);

  const innerCount = useSelect(
    (select) =>
      select('core/block-editor').getBlock(clientId)?.innerBlocks?.length ?? 0,
    [clientId]
  );

  // Inserter/preview renders go through the same edit component; the appender
  // is an authoring affordance and would just be dead chrome inside a preview.
  const isPreviewMode = useSelect(
    (select) => !!select(blockEditorStore).getSettings().isPreviewMode,
    []
  );

  // Live preview: fetch one real post matching the configured postType so the
  // post-* inner blocks render with actual content. Editor shows how the
  // child template (featured image / title / excerpt + size + level + style)
  // looks against real data. Frontend still renders N posts via render.php.
  const previewPost = useSelect(
    (select) => {
      if (!isDynamic) return null;
      const records = select(coreStore).getEntityRecords('postType', previewPostType, {
        per_page: 1,
        _embed: 1,
        status: 'publish',
        orderby: 'date',
        order: 'desc',
      });
      return records?.[0] ?? null;
    },
    [isDynamic, previewPostType]
  );

  const previewContext = previewPost
    ? { postId: previewPost.id, postType: previewPostType }
    : null;

  // Inherit ("") cannot be resolved client-side — theme.json and the
  // brandy_slider_controls filter both run server-side — so the canvas shows the
  // built-in default until the editor picks an explicit placement.
  const isSlider = displayMode === 'slider';
  const placement = attributes.controlsPlacement || 'default';

  const blockProps = useBlockProps({
    className: `brandy-media-gallery is-display-${displayMode} is-title-${titlePosition}${
      isSlider ? ' is-editor-preview' : ''
    }${isSlider ? ` ${navHostClass(attributes.controlsNav)}`.trimEnd() : ''}`,
    ...(isSlider ? { 'data-controls-placement': placement } : {}),
    style: {
      '--brandy-mgal-cols-desktop': columns?.desktop ?? 3,
      '--brandy-mgal-cols-tablet': columns?.tablet ?? 2,
      '--brandy-mgal-cols-mobile': columns?.mobile ?? 1,
      /*
       * Mirrors render.php: core's Block spacing wins, the block's own legacy
       * `gap` attribute is the fallback for galleries built before the support
       * existed.
       *
       * The unset default differs by mode because the front end's does. A
       * slider is spaced by embla.min.css, whose --embla-gap falls back to
       * 30px; the grid falls back to this block's own 16px. Matching them here
       * is what stops the canvas showing a gap the visitor will not get.
       */
      '--brandy-mgal-gap': blockGap || gap || (isSlider ? '30px' : '16px'),
      // Only in slider mode, and only on this element: `is-editor-preview` is
      // where embla.min.css seeds the button colours, and an element's own
      // declaration beats an inherited one.
      ...(isSlider ? controlColorVars(attributes) : {}),
    },
  });

  const innerBlocksProps = useInnerBlocksProps(
    { className: 'brandy-media-gallery__items' },
    {
      allowedBlocks: [ITEM],
      template: isDynamic ? [[ITEM, {}, DYNAMIC_INNER]] : STATIC_TEMPLATE,
      renderAppender: false,
    }
  );

  return (
    <>
      <InspectorControls>
        <SourcePanel attributes={attributes} setAttributes={setAttributes} />
        {isDynamic && (
          <QueryPanel attributes={attributes} setAttributes={setAttributes} />
        )}
        <DisplayPanel attributes={attributes} setAttributes={setAttributes} />
        {displayMode === 'slider' && (
          <PanelBody title={__('Slider controls', 'brandy-blocks')} initialOpen={false}>
            <SliderControlsPanel attributes={attributes} setAttributes={setAttributes} />
          </PanelBody>
        )}
        <TitlePositionPanel attributes={attributes} setAttributes={setAttributes} />
        <LightboxPanel attributes={attributes} setAttributes={setAttributes} />
      </InspectorControls>
      <div {...blockProps}>
        {isDynamic ? (
          <>
            <Notice status="info" isDismissible={false}>
              {previewPost
                ? sprintf(
                    /* translators: 1: previewed post title, 2: configured perPage */
                    __(
                      'Preview shown using "%1$s". %2$d post(s) will render on the frontend.',
                      'brandy-blocks'
                    ),
                    previewPost?.title?.rendered || __('(untitled)', 'brandy-blocks'),
                    perPage
                  )
                : __(
                    'Dynamic gallery — no posts of the configured type were found for the editor preview.',
                    'brandy-blocks'
                  )}
            </Notice>
            {previewContext ? (
              <BlockContextProvider value={previewContext}>
                <div {...innerBlocksProps} />
              </BlockContextProvider>
            ) : (
              <Placeholder
                icon={galleryIcon}
                label={__('Dynamic gallery', 'brandy-blocks')}
                instructions={sprintf(
                  /* translators: %d is the configured perPage count */
                  __(
                    '%d post(s) of type "%s" will render on the frontend. Add a post of this type to enable the editor preview.',
                    'brandy-blocks'
                  ),
                  perPage,
                  previewPostType
                )}
              />
            )}
          </>
        ) : (
          <>
            {innerCount >= SOFT_WARN_THRESHOLD && (
              <Notice status="warning" isDismissible={false}>
                {sprintf(
                  /* translators: %d is the number of items in the gallery */
                  __(
                    'This gallery has %d items. Performance in the editor may degrade. Consider splitting into multiple galleries.',
                    'brandy-blocks'
                  ),
                  innerCount
                )}
              </Notice>
            )}
            <div {...innerBlocksProps} />
            {!isPreviewMode && <ItemAppender clientId={clientId} />}
          </>
        )}
        {isSlider && (
          <SliderControlsSkeleton
            placement={placement}
            dotsStyle={attributes.controlsDots || 'default'}
            dotCount={previewDotCount(innerCount, columns?.desktop)}
          />
        )}
      </div>
    </>
  );
}
